import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@parrot-software/design-system';

type StepTransitionProps = {
  children: ReactNode;
  direction: 'forward' | 'backward';
  stepKey: string;
};

/**
 * Wraps wizard step content with a slide + fade transition.
 * Animates out the current step and animates in the new step on stepKey change.
 */
export const StepTransition = ({ children, direction, stepKey }: StepTransitionProps) => {
  const [displayedChildren, setDisplayedChildren] = useState(children);
  const [phase, setPhase] = useState<'enter' | 'exit'>('enter');
  const [currentDirection, setCurrentDirection] = useState(direction);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevStepKeyRef = useRef(stepKey);
  const pendingChildrenRef = useRef(children);

  pendingChildrenRef.current = children;

  // Update displayed children immediately when step hasn't changed (user interactions within a step)
  useEffect(() => {
    if (stepKey === prevStepKeyRef.current) {
      setDisplayedChildren(children);
    }
  }, [children, stepKey]);

  // Animate only when stepKey changes (actual step navigation)
  useEffect(() => {
    if (stepKey === prevStepKeyRef.current) return;

    prevStepKeyRef.current = stepKey;
    setCurrentDirection(direction);
    setPhase('exit');

    timeoutRef.current = setTimeout(() => {
      setDisplayedChildren(pendingChildrenRef.current);
      setPhase('enter');
    }, 200);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [stepKey, direction]);

  const isForward = currentDirection === 'forward';
  const isEntering = phase === 'enter';

  return (
    <div
      className={cn('flex flex-1 flex-col overflow-x-hidden transition-[opacity,transform] duration-200 ease-in-out', {
        'opacity-0': !isEntering,
        '-translate-x-4': !isEntering && isForward,
        'translate-x-4': !isEntering && !isForward,
        'translate-x-0 opacity-100': isEntering,
      })}
    >
      {displayedChildren}
    </div>
  );
};
